import { ApiReportSuggestionGenerationStatusChoices } from "@api/graphql/types-and-hooks";
import { useIsQaScanActive } from "@components/QaReview/hooks/useIsQaScanActive";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useIsApprovalRequired } from "@components/ReportReview/hooks/useIsApprovalRequired";
import { useReportReviewers } from "@components/ReportReview/hooks/useReportReviewers";
import { getApprovalBlockReason } from "@components/ReportReview/reviewApproval";
import { useReport } from "@providers/ReportProvider";

// PROA-1493: shown on the disabled "complete/sign" button when resolution is
// required and active suggestions remain unresolved.
export const QA_RESOLUTION_REQUIRED_TOOLTIP =
  "All Quality Review suggestions must be resolved before you can complete this report.";

interface ReviewGateArgs {
  /** template.requireAllQaResolved || org.requireAllQaResolved */
  requireAllQaResolved: boolean | undefined;
  /** true only on a review-and-sign page where the QA scanner actually runs */
  qaEnforced: boolean;
  /** true once we can trust the suggestion set: it loaded, or the QA workflow failed */
  qaStateKnown: boolean;
  hasOpenQaItems: boolean;
}

/**
 * PROA-1493: the core review-and-sign gate. Blocks advancing when resolution is
 * required and enforced, and either (a) the QA state isn't known yet — so we
 * fail CLOSED while suggestions are still loading rather than let a report
 * through before we know — or (b) a resolvable suggestion is still unresolved.
 *
 * When the setting is off, QA isn't enforced for this report, or every
 * resolvable suggestion is resolved, advancing is allowed.
 */
export const isReviewGateBlocked = ({
  requireAllQaResolved,
  qaEnforced,
  qaStateKnown,
  hasOpenQaItems,
}: ReviewGateArgs) => {
  if (!requireAllQaResolved || !qaEnforced) return false;
  if (!qaStateKnown) return true;
  return hasOpenQaItems;
};

interface ReviewGate {
  isBlocked: boolean;
  tooltipReason: string | undefined;
}

export const useReviewGate = (): ReviewGate => {
  const { report } = useReport();
  const { openQaItems, isQaLoaded, status } = useReviewData();
  const isQaScanActive = useIsQaScanActive();
  const isApprovalRequired = useIsApprovalRequired();
  const { reviewers, isLoaded, isError } = useReportReviewers();

  const isQaBlocked = isReviewGateBlocked({
    requireAllQaResolved: !!(report.reportTemplate?.requireAllQaResolved || report.organization?.requireAllQaResolved),
    qaEnforced: isQaScanActive,
    qaStateKnown: isQaLoaded || status === ApiReportSuggestionGenerationStatusChoices.Failure,
    hasOpenQaItems: openQaItems.length > 0,
  });
  const approvalReason = getApprovalBlockReason({
    isRequired: isApprovalRequired && !isError,
    isLoaded,
    reviewers,
  });

  return {
    isBlocked: isQaBlocked || !!approvalReason,
    tooltipReason: isQaBlocked ? QA_RESOLUTION_REQUIRED_TOOLTIP : approvalReason,
  };
};
